import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Send, Mail, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RichEditor } from "@/components/RichEditor";

export default function EmailTemplates() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const isAdmin = user?.role === "admin";

  const [templates, setTemplates] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ html: "", subject: "" });
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocused = useRef("body");

  useEffect(() => {
    api.get("/email-templates")
      .then((r) => {
        setTemplates(r.data);
        if (r.data.length) { setActiveKey(r.data[0].key); setDraft(r.data[0]); }
      })
      .catch(() => setTemplates([]));
  }, []);

  const active = templates?.find((t) => t.key === activeKey);

  const selectTemplate = (t) => {
    setActiveKey(t.key);
    setDraft(t);
  };

  const setField = (field, val) => setDraft((d) => ({ ...d, [field]: val }));

  const runPreview = useCallback(async () => {
    if (!activeKey || !draft) return;
    try {
      const r = await api.post(`/email-templates/${activeKey}/preview`, {
        lang,
        subject: draft[`subject_${lang}`] || "",
        body: draft[`body_${lang}`] || "",
      });
      setPreview(r.data);
    } catch (e) {
      /* preview is best-effort */
    }
  }, [activeKey, draft, lang]);

  useEffect(() => {
    const id = setTimeout(runPreview, 400);
    return () => clearTimeout(id);
  }, [runPreview]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/email-templates/${activeKey}`, {
        subject_en: draft.subject_en || "",
        subject_fr: draft.subject_fr || "",
        body_en: draft.body_en || "",
        body_fr: draft.body_fr || "",
      });
      setTemplates((ts) => ts.map((t) => (t.key === activeKey ? r.data : t)));
      setDraft(r.data);
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      await api.post(`/email-templates/${activeKey}/test`, { to: testTo, lang });
      toast.success(`Test "${active?.label}" sent to ${testTo}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const insertVar = (v) => {
    const token = `{{${v}}}`;
    if (lastFocused.current === "subject" && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = el.value.slice(0, start) + token + el.value.slice(end);
      setField(`subject_${lang}`, next);
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
    } else {
      bodyRef.current?.insertText(token);
    }
  };

  if (!templates) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>;

  const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60";

  return (
    <div data-testid="admin-email-templates">
      <h1 className="font-display font-black uppercase text-3xl flex items-center gap-3"><Mail className="text-[#2CACD4]" /> Email Templates</h1>
      <p className="mt-1 text-muted-foreground text-sm">Edit the wording of every automated email. Content is wrapped in the branded, site-styled shell (logo, colours, footer) before sending. Use the variable chips to insert personalised fields.</p>
      {!canEdit && <p className="mt-4 rounded-xl bg-muted p-3 text-sm text-muted-foreground">You have read-only access to templates.</p>}

      <div className="mt-6 grid gap-6 lg:grid-cols-[230px_1fr]">
        {/* Template list */}
        <div className="flex flex-col gap-1.5" data-testid="template-list">
          {templates.map((t) => (
            <button
              key={t.key}
              onClick={() => selectTemplate(t)}
              className={`text-left rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${activeKey === t.key ? "bg-[#0F172A] text-white" : "bg-card border border-border hover:border-[#2CACD4]"}`}
              data-testid={`template-tab-${t.key}`}
            >
              {t.label}
              <span className={`block text-xs font-normal mt-0.5 ${activeKey === t.key ? "text-white/60" : "text-muted-foreground"}`}>{t.recipient}</span>
            </button>
          ))}
        </div>

        {/* Editor */}
        {draft && (
          <div className="min-w-0">
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">{active?.description}</p>

              <div className="mt-4 flex items-center gap-2">
                {["en", "fr"].map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors cursor-pointer ${lang === l ? "bg-[#2CACD4] text-slate-950" : "bg-muted text-muted-foreground hover:text-foreground"}`} data-testid={`lang-${l}`}>
                    {l === "en" ? "English" : "Français"}
                  </button>
                ))}
              </div>

              {!!(active?.variables?.length) && (
                <div className="mt-4">
                  <span className="block text-xs font-semibold text-muted-foreground mb-1.5">Insert a variable</span>
                  <div className="flex flex-wrap gap-1.5">
                    {active.variables.map((v) => (
                      <button key={v} type="button" disabled={!canEdit} onClick={() => insertVar(v)} className="rounded-full bg-[#CAE295]/50 text-green-900 px-2.5 py-1 text-xs font-bold hover:bg-[#CAE295] transition-colors cursor-pointer disabled:opacity-50" data-testid={`var-chip-${v}`}>
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="block text-sm font-semibold mt-5 mb-1.5">Subject ({lang.toUpperCase()})</label>
              <input
                ref={subjectRef}
                disabled={!canEdit}
                value={draft[`subject_${lang}`] || ""}
                onFocus={() => (lastFocused.current = "subject")}
                onChange={(e) => setField(`subject_${lang}`, e.target.value)}
                className={inputCls}
                data-testid="template-subject"
              />

              <label className="block text-sm font-semibold mt-5 mb-1.5">Body ({lang.toUpperCase()})</label>
              <div onFocus={() => (lastFocused.current = "body")} className={!canEdit ? "pointer-events-none opacity-70" : ""}>
                <RichEditor ref={bodyRef} value={draft[`body_${lang}`] || ""} onChange={(v) => setField(`body_${lang}`, v)} />
              </div>

              {canEdit && (
                <button onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-3 transition-colors cursor-pointer disabled:opacity-60" data-testid="template-save">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Save template</>}
                </button>
              )}
            </div>

            {/* Live preview */}
            <div className="mt-6 rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display font-black uppercase text-lg flex items-center gap-2"><Eye size={18} className="text-[#2CACD4]" /> Live preview</h2>
              <p className="mt-1 text-sm text-muted-foreground">Sample data shown · Subject: <strong className="text-foreground">{preview.subject}</strong></p>
              <iframe title="email-preview" srcDoc={preview.html} className="mt-3 w-full h-[560px] rounded-xl border border-border bg-white" data-testid="template-preview" />
            </div>

            {/* Send test */}
            {isAdmin && (
              <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                <h2 className="font-display font-black uppercase text-lg">Send a test</h2>
                <p className="mt-1 text-sm text-muted-foreground">Sends this template (with sample data) using your saved SMTP settings.</p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourdomain.ca" className={inputCls} data-testid="template-test-to" />
                  <button onClick={sendTest} disabled={testing} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#DB1A5C] text-white hover:bg-[#0F172A] font-bold px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap" data-testid="template-test-send">
                    {testing ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Send test</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
